import { Box, CircularProgress } from "@material-ui/core";
import { Button } from "components";
import { useFormik } from "formik";
import { usePopup } from "hooks/usePopup";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import productShelfLifeService from "services/productShelfLifeService";
import AutoCompleteProductBaseFormik from "shared/AutoCompleteProductBaseFormik";
import Input from "shared/Input";

import {
  Actions,
  FormContent,
  FormTitle,
  SaveButton,
  StyledDrawer
} from "../styles";

const emptyForm = {
  shelfLifeA: "",
  shelfLifeB: "",
  shelfLifeC: ""
};

const getProductLabel = product => `${product.id} - ${product.name}`;
const getProductBaseKey = productBaseId =>
  productBaseId === undefined || productBaseId === null || productBaseId === ""
    ? ""
    : String(productBaseId);

export const ShelfLifeDrawer = ({
  open,
  close,
  onExited,
  product,
  onSaved
}) => {
  const { addPopup } = usePopup();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadedProductBaseId, setLoadedProductBaseId] = useState("");
  const loadRequestRef = useRef(0);
  const formik = useFormik({
    initialValues: { productBase: null },
    enableReinitialize: true,
    onSubmit: () => {}
  });

  const selectedProduct = useMemo(() => {
    if (product) return product;
    return formik.values.productBase;
  }, [product, formik.values.productBase]);

  const selectedProductKey = getProductBaseKey(selectedProduct?.id);

  const loadShelfLife = useCallback(
    async productBaseId => {
      const productBaseKey = getProductBaseKey(productBaseId);
      const requestId = loadRequestRef.current + 1;
      loadRequestRef.current = requestId;
      const isCurrentRequest = () => loadRequestRef.current === requestId;

      if (!productBaseKey) {
        setLoading(false);
        setForm(emptyForm);
        setLoadedProductBaseId("");
        return;
      }

      try {
        setLoading(true);
        setForm(emptyForm);
        setLoadedProductBaseId("");
        const { data } = await productShelfLifeService.getByProductBaseId(
          productBaseId
        );

        if (!isCurrentRequest()) return;

        setForm({
          shelfLifeA: data.shelfLifeA ?? "",
          shelfLifeB: data.shelfLifeB ?? "",
          shelfLifeC: data.shelfLifeC ?? ""
        });
        setLoadedProductBaseId(productBaseKey);
      } catch (error) {
        if (!isCurrentRequest()) return;

        if (error?.response?.status === 404) {
          setForm(emptyForm);
          setLoadedProductBaseId(productBaseKey);
          return;
        }

        setLoadedProductBaseId("");
        addPopup({
          type: "error",
          title: "Erro ao carregar shelf life do produto."
        });
      } finally {
        if (isCurrentRequest()) {
          setLoading(false);
        }
      }
    },
    [addPopup]
  );

  useEffect(() => {
    if (!open) return;

    if (product) {
      const productBaseKey = getProductBaseKey(product.id);
      loadRequestRef.current += 1;
      setLoading(false);
      setForm({
        shelfLifeA: product.shelfLifeA ?? "",
        shelfLifeB: product.shelfLifeB ?? "",
        shelfLifeC: product.shelfLifeC ?? ""
      });
      setLoadedProductBaseId(productBaseKey);
      return;
    }

    loadShelfLife(selectedProduct?.id);
  }, [loadShelfLife, open, product, selectedProduct]);

  useEffect(() => {
    if (!open) {
      loadRequestRef.current += 1;
    }
  }, [open]);

  const handleExited = () => {
    formik.resetForm();
    setForm(emptyForm);
    setLoadedProductBaseId("");
    setLoading(false);
    onExited?.();
  };

  const handleChange = field => event => {
    setForm(state => ({ ...state, [field]: event.target.value }));
  };

  const validate = () => {
    if (!selectedProduct?.id) {
      addPopup({ type: "error", title: "Selecione um produto base." });
      return false;
    }

    if (loadedProductBaseId !== selectedProductKey) {
      addPopup({
        type: "error",
        title: "Aguarde carregar o shelf life do produto selecionado."
      });
      return false;
    }

    const values = [form.shelfLifeA, form.shelfLifeB, form.shelfLifeC].map(
      Number
    );
    const hasInvalidValue = values.some(
      value => !Number.isFinite(value) || value <= 0
    );

    if (hasInvalidValue) {
      addPopup({
        type: "error",
        title: "Informe valores maiores que zero para A, B e C."
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setSaving(true);
      await productShelfLifeService.save(selectedProduct.id, {
        shelfLifeA: Number(form.shelfLifeA),
        shelfLifeB: Number(form.shelfLifeB),
        shelfLifeC: Number(form.shelfLifeC)
      });
      addPopup({ type: "success", title: "Shelf life salvo com sucesso." });
      onSaved();
      close();
    } catch (error) {
      addPopup({
        type: "error",
        title:
          error?.response?.data?.msg ||
          error?.response?.data?.detail ||
          "Erro ao salvar shelf life."
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <StyledDrawer
      anchor="right"
      open={open}
      onClose={close}
      SlideProps={{ onExited: handleExited }}
      title="Shelf life por produto"
    >
      <FormContent>
        <FormTitle>
          {selectedProduct
            ? getProductLabel(selectedProduct)
            : "Selecione um produto base"}
        </FormTitle>

        {!product && (
          <AutoCompleteProductBaseFormik
            formik={formik}
            name="productBase"
            label="Produto base *"
          />
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" padding="2rem">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Input
              label="Classe A (dias)"
              variant="outlined"
              type="number"
              value={form.shelfLifeA}
              onChange={handleChange("shelfLifeA")}
            />
            <Input
              label="Classe B (dias)"
              variant="outlined"
              type="number"
              value={form.shelfLifeB}
              onChange={handleChange("shelfLifeB")}
            />
            <Input
              label="Classe C (dias)"
              variant="outlined"
              type="number"
              value={form.shelfLifeC}
              onChange={handleChange("shelfLifeC")}
            />
          </>
        )}

        <Actions>
          <Button
            fullWidth
            variant="outlined"
            onClick={close}
            disabled={saving}
          >
            Cancelar
          </Button>
          <SaveButton
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            isLoading={saving}
            disabled={
              loading ||
              !selectedProductKey ||
              loadedProductBaseId !== selectedProductKey
            }
          >
            Salvar
          </SaveButton>
        </Actions>
      </FormContent>
    </StyledDrawer>
  );
};
